import createClient, { type Middleware } from "openapi-fetch";
import type { components, paths } from "./generated/schema";
import { ApiError, apiBaseUrl, applyRequestHeaders, handleUnauthorized, toApiError } from "./http";

export type { components, paths };

/** A DTO from the OpenAPI document: `Schema<"PolicyDetail">`. */
export type Schema<K extends keyof components["schemas"]> = components["schemas"][K];

const platformMiddleware: Middleware = {
  onRequest({ request }) {
    applyRequestHeaders(request.method, request.headers);
    return request;
  },
  async onResponse({ request, response }) {
    if (response.status !== 401) return response;
    // A step-up challenge (401 `step_up_required`) is answered in place by re-authenticating; the session itself is still valid.
    if ((await toApiError(response.clone())).code === "step_up_required") return response;
    handleUnauthorized(new URL(request.url, typeof window !== "undefined" ? window.location.origin : "http://localhost").pathname, response.status);
    return response;
  },
};

function build() {
  const client = createClient<paths>({ baseUrl: apiBaseUrl(), credentials: "include" });
  client.use(platformMiddleware);
  return client;
}

/**
 * Typed client generated from the OpenAPI document. Paths, parameters and bodies are checked against
 * `src/generated/schema.d.ts`. Use {@link unwrap} to turn `{ data, error, response }` into data or an {@link ApiError}.
 */
export const typedApi = build();

/**
 * Resolves an openapi-fetch call to its data, or throws an {@link ApiError} (status 0 when the service could not be reached).
 *
 * `const session = await unwrap(typedApi.GET("/api/auth/session"));`
 */
export async function unwrap<T>(call: Promise<{ data?: T; error?: unknown; response: Response }>): Promise<T> {
  let result: { data?: T; error?: unknown; response: Response };
  try {
    result = await call;
  } catch (e) {
    if (e instanceof DOMException && e.name === "AbortError") throw e;
    throw new ApiError(0);
  }
  const { data, error, response } = result;
  if (!response.ok) {
    if (error && typeof error === "object") return Promise.reject(new ApiError(response.status, error as never));
    throw await toApiError(response);
  }
  return data as T;
}
